// frontend/src/pages/SpriterDashboard.jsx

import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Input,
  Label,
  Row,
  Col,
  Form,
} from "reactstrap";
import PopupModal from "../../components/Common/PopupModal";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import AnimatedMapSprite from "../Maps/logic/AnimimatedMapSprite";

const SpriterDashboard = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState("1");

  // Popup state
  const [popup, setPopup] = useState({
    isOpen: false,
    message: "",
    backgroundColor: "#1d222e",
  });

  // -------------- Authorization Check --------------
  useEffect(() => {
    const checkRole = async () => {
      try {
        const res = await api.get("/v1/admin/rank", { withCredentials: true });
        const admValue = res.data[0]?.adm;
        if (admValue !== 1 && admValue !== 2) {
          navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        navigate("/dashboard", { replace: true });
      }
    };
    checkRole();
  }, [navigate]);

  // -------------- Shared “allPokemon” List (existing front‐sprites) --------------
  const [allPokemon, setAllPokemon] = useState([]); // [{ name: "Bulbasaur" }, …]
  const [pokemonSearch, setPokemonSearch] = useState("");
  const [filteredPokemon, setFilteredPokemon] = useState([]);

  // -------------- Sprite Manager State --------------
  // selectedName is either typed or chosen from the dropdown
  const [selectedName, setSelectedName] = useState("");
  const [spriteStatus, setSpriteStatus] = useState({
    front: null,
    back: null,
    icon: null,
    overworld: null,   // ← holds "/Maps/Overworld/<name>.png" or null
  });
  const [uploadType, setUploadType] = useState("front");
  const [uploadFile, setUploadFile] = useState(null);

  // -------------- DB Manager State (Insert Only) --------------
  const initialDBValues = {
    id: "",
    name: "",
    pic: "",
    hp: "",
    att: "",
    def_: "",
    speed: "",
    specatt: "",
    specdef: "",
    minipic: "",
    evolve: "",
    pokevol: "",
    type1: "",
    swap: "",
    spriter: "",
    AncientGroup: "",
    map: "",
  };
  const [dbInitialValues, setDbInitialValues] = useState(initialDBValues);

  // -------------- Helpers --------------
  const handlePopup = (message, backgroundColor = "#1d222e") => {
    setPopup({ isOpen: true, message, backgroundColor });
    setTimeout(() => setPopup((prev) => ({ ...prev, isOpen: false })), 2000);
  };
  const toggle = (id) => setOpen(open === id ? "" : id);

  // Utility: if user typed in textbox, use that; otherwise use dropdown
  const getCurrentName = () => {
    return selectedName || pokemonSearch.trim();
  };

  // -------- Fetch all existing front‐sprite names ------------
  useEffect(() => {
    const fetchAllSprites = async () => {
      try {
        const res = await api.get("/v1/spriter/sprites/list", {
          withCredentials: true,
        });
        // res.data === [ { name: "Bulbasaur" }, … ]
        setAllPokemon(res.data);
        setFilteredPokemon(res.data);
      } catch (err) {
        console.error("Failed to fetch sprite list:", err);
      }
    };
    fetchAllSprites();
  }, []);

  // -------- Filter dropdown suggestions for sprites ------------
  useEffect(() => {
    const lower = pokemonSearch.toLowerCase();
    setFilteredPokemon(
      allPokemon.filter((p) => p.name.toLowerCase().includes(lower))
    );
  }, [pokemonSearch, allPokemon]);

  // -------- Fetch sprite status (front/back/icon/overworld) for current name ------------
  useEffect(() => {
    const nameToUse = getCurrentName();
    if (!nameToUse) {
      setSpriteStatus({ front: null, back: null, icon: null, overworld: null });
      return;
    }
    (async () => {
      try {
        const res = await api.get(
          `/v1/spriter/pokemon/${encodeURIComponent(nameToUse)}/sprites`,
          { withCredentials: true }
        );
        // res.data === { front: string|null, back: string|null, icon: string|null, overworld: string|null }
        setSpriteStatus(res.data);
      } catch (err) {
        console.error("Failed to fetch sprite status:", err);
        setSpriteStatus({ front: null, back: null, icon: null, overworld: null });
      }
    })();
  }, [selectedName, pokemonSearch]);

  // -------------- Sprite Management Handlers --------------
  const handleDeleteSprite = async (type) => {
    const nameToUse = getCurrentName();
    if (!nameToUse) {
      handlePopup("Enter a Pokémon name first.", "red");
      return;
    }
    try {
      await api.delete(
        `/v1/spriter/pokemon/${encodeURIComponent(nameToUse)}/sprite`,
        {
          params: { type },
          withCredentials: true,
        }
      );
      handlePopup(`Deleted ${type} sprite for ${nameToUse}.`, "#1d222e");
      setSpriteStatus((prev) => ({ ...prev, [type]: null }));
    } catch (err) {
      handlePopup(
        err.response?.data.detail || `Failed to delete ${type} sprite.`,
        "red"
      );
    }
  };

  const handleUploadSprite = async () => {
    const nameToUse = getCurrentName();
    if (!nameToUse) {
      handlePopup("Enter a Pokémon name first.", "red");
      return;
    }
    if (!uploadFile) {
      handlePopup("Choose a PNG to upload.", "red");
      return;
    }
    // enforce ≤ 1 MB
    if (uploadFile.size > 1024 * 1024) {
      handlePopup("Maximum upload size is 1 MB.", "red");
      return;
    }

    const form = new FormData();
    form.append("file", uploadFile);

    try {
      await api.post(
        `/v1/spriter/pokemon/${encodeURIComponent(nameToUse)}/sprite`,
        form,
        {
          params: { type: uploadType },
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );
      handlePopup(`Uploaded ${uploadType} sprite for ${nameToUse}.`, "#1d222e");
      setUploadFile(null);

      // Immediately update the preview URL in UI:
      const publicPath =
        uploadType === "front"
          ? `/sprites/${nameToUse}.png`
          : uploadType === "back"
          ? `/BackSprites/${nameToUse}.png`
          : uploadType === "icon"
          ? `/icon/${nameToUse}.png`
          : `/Maps/Overworld/${nameToUse}.png`; // overworld

      setSpriteStatus((prev) => ({ ...prev, [uploadType]: publicPath }));
    } catch (err) {
      handlePopup(
        err.response?.data.detail || `Failed to upload ${uploadType} sprite.`,
        "red"
      );
    }
  };

  // -------------- DB Management Handlers (Insert Only) --------------
  const dbSchema = Yup.object({
    id: Yup.number().integer().positive().required("Required"),
    name: Yup.string().required("Required"),
    pic: Yup.string().required("Required"),
    hp: Yup.number().integer().min(0).required("Required"),
    att: Yup.number().integer().min(0).required("Required"),
    def_: Yup.number().integer().min(0).required("Required"),
    speed: Yup.number().integer().min(0).required("Required"),
    specatt: Yup.number().integer().min(0).required("Required"),
    specdef: Yup.number().integer().min(0).required("Required"),
    minipic: Yup.string().required("Required"),
    evolve: Yup.string().required("Required"),
    pokevol: Yup.string().required("Required"),
    type1: Yup.string().required("Required"),
    swap: Yup.string().required("Required"),
    spriter: Yup.string().required("Required"),
    AncientGroup: Yup.string().required("Required"),
    map: Yup.string().required("Required"),
  });

  const handleDBSubmit = async (values, actions) => {
    try {
      // Always POST (insert or update) to the same endpoint
      await api.post("/v1/spriter/pokemon", values, {
        withCredentials: true,
      });
      handlePopup(`Saved Pokémon: ${values.name} (ID ${values.id}).`, "#1d222e");
      actions.resetForm();
      setDbInitialValues(initialDBValues);
    } catch (err) {
      handlePopup(err.response?.data.detail || "Failed to submit Pokémon data.", "red");
    }
  };

  return (
    <div className="page-content" style={{ marginTop: "2rem" }}>
      <Accordion open={open} toggle={toggle}>
        {/* 1. Sprite Manager */}
        <AccordionItem>
          <AccordionHeader targetId="1">Sprite Manager</AccordionHeader>
          <AccordionBody accordionId="1">
            <h5>Enter or Select Pokémon to Manage Sprites</h5>
            <Row className="mb-3">
              {/* 1) Free-text box for any name */}
              <Col md="4">
                <Input
                  type="text"
                  placeholder="Type a Pokémon name..."
                  value={pokemonSearch}
                  onChange={(e) => {
                    setPokemonSearch(e.target.value);
                    setSelectedName("");
                  }}
                />
              </Col>
              {/* 2) Dropdown of “existing” names (from /public/sprites) */}
              <Col md="4">
                <Input
                  type="select"
                  value={selectedName}
                  onChange={(e) => setSelectedName(e.target.value)}
                >
                  <option value="">-- Select Pokémon --</option>
                  {filteredPokemon.map((p) => (
                    <option key={p.name} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </Input>
                <div className="small text-muted mt-1">
                  Or type any new name (e.g. “RubyMewtwo”)
                </div>
              </Col>
            </Row>

            {getCurrentName() && (
              <>
                <h6>Current Sprites for {getCurrentName()}</h6>
                <Row className="mb-3">
                  {/*  Front Sprite  */}
                  <Col md="3" className="text-center">
                    <Label>Front</Label>
                    {spriteStatus.front ? (
                      <img
                        src={spriteStatus.front}
                        alt={`${getCurrentName()} front`}
                        style={{ width: 128, height: 128 }}
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    ) : (
                      <p>No front</p>
                    )}
                    {spriteStatus.front && (
                      <Button
                        color="danger"
                        size="sm"
                        className="mt-2"
                        onClick={() => handleDeleteSprite("front")}
                      >
                        Delete
                      </Button>
                    )}
                  </Col>

                  {/*  Back Sprite  */}
                  <Col md="3" className="text-center">
                    <Label>Back</Label>
                    {spriteStatus.back ? (
                      <img
                        src={spriteStatus.back}
                        alt={`${getCurrentName()} back`}
                        style={{ width: 128, height: 128 }}
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    ) : (
                      <p>No back</p>
                    )}
                    {spriteStatus.back && (
                      <Button
                        color="danger"
                        size="sm"
                        className="mt-2"
                        onClick={() => handleDeleteSprite("back")}
                      >
                        Delete
                      </Button>
                    )}
                  </Col>

                  {/*  Icon (minipic)  */}
                  <Col md="3" className="text-center">
                    <Label>Icon</Label>
                    {spriteStatus.icon ? (
                      <img
                        src={spriteStatus.icon}
                        alt={`${getCurrentName()} icon`}
                        style={{ maxWidth: "100%", height: "50px" }}
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    ) : (
                      <p>No icon</p>
                    )}
                    {spriteStatus.icon && (
                      <Button
                        color="danger"
                        size="sm"
                        className="mt-2"
                        onClick={() => handleDeleteSprite("icon")}
                      >
                        Delete
                      </Button>
                    )}
                  </Col>

                  {/*  Overworld Map  */}
                  <Col md="3" className="text-center">
                    <Label>Overworld</Label>
                    {spriteStatus.overworld ? (
                      <>
                        <AnimatedMapSprite
                          pokeName={getCurrentName()}
                          style={{ width: 32, height: 32 }}
                        />
                        <div className="mt-2">
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDeleteSprite("overworld")}
                          >
                            Delete
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p>No overworld</p>
                    )}
                  </Col>
                </Row>

                <h6>Upload / Overwrite Sprite (≤ 1 MB, PNG only)</h6>
                <Form inline className="mb-3">
                  <Row>
                    <Col md="3">
                      <Input
                        type="select"
                        value={uploadType}
                        onChange={(e) => setUploadType(e.target.value)}
                      >
                        <option value="front">Front</option>
                        <option value="back">Back</option>
                        <option value="icon">Icon</option>
                        <option value="overworld">Overworld</option>
                      </Input>
                    </Col>
                    <Col md="5">
                      <Input
                        type="file"
                        accept="image/png"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                      />
                      <div className="small text-muted mt-1">
                        (PNG only, max 1 MB)
                      </div>
                    </Col>
                    <Col md="2">
                      <Button color="primary" onClick={handleUploadSprite}>
                        Upload
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </>
            )}
          </AccordionBody>
        </AccordionItem>

      </Accordion>

      <PopupModal
        isOpen={popup.isOpen}
        message={popup.message}
        backgroundColor={popup.backgroundColor}
        onClose={() => setPopup((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default SpriterDashboard;
